"use client";

import { useEffect, useState } from "react";
import { useUser } from "@/lib/context/user-context";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/lib/i18n";

export function CourseHeaderProgress({ slug }: { slug: string }) {
  const { user } = useUser();
  const [state, setState] = useState<{ total: number; completed: number; percent: number } | null>(null);
  const { t } = useI18n();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user?.id) return;
      try {
        const url = `/api/course/progress?userId=${encodeURIComponent(user.id)}&slug=${encodeURIComponent(slug)}`;
        const res = await fetch(url, { cache: 'no-store' });
        const json = await res.json();
        const total = parseInt(json?.meta?.total_idioms || json?.all?.['meta:total_idioms'] || '0', 10) || 0;
        const completed = parseInt(json?.meta?.completed_idioms || json?.all?.['meta:completed_idioms'] || '0', 10) || 0;
        const percent = parseInt(json?.meta?.percent || json?.all?.['meta:percent'] || '0', 10) || 0;
        if (!cancelled) setState({ total, completed, percent });
      } catch {
        // ignore
      }
    }
    load();
    const onSaved = () => load();
    window.addEventListener('shadowingRecordSaved', onSaved as any);
    return () => { cancelled = true; window.removeEventListener('shadowingRecordSaved', onSaved as any); };
  }, [user?.id, slug]);

  if (!user?.id || !state) return null;

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">{t("course.progress.headerLabel")}</span>
      <Badge variant="secondary" className="rounded-full px-2 py-0.5">{state.completed} / {state.total}</Badge>
      <Badge className="rounded-full px-2 py-0.5">{state.percent}%</Badge>
    </div>
  );
}
